/**
 * The phone's baseline `inputProps` — the REAL composition's props built from
 * what the wizard has collected (colours, faces, logo, language) over the
 * committed sample clip.
 *
 * Deliberately NOT keyed on the design: the phone replays the steps' patch ON
 * TOP of this baseline, so a dependency on it would rebuild the baseline under
 * the patch on every step (the trap `Onboard.tsx` documents).
 */
import { useEffect, useMemo, useState } from "react";
import type { ClipProps, FontFaceSpec, RenderLayout } from "../../../../../src/remotion/types.ts";
import { buildSampleInputProps } from "../../../lib/onboardingSample";
import { cachedLogoPlaceholder, drawLogoPlaceholder, logoPlaceholderSvgDataUrl } from "../../../lib/brandSetup/logoPlaceholder";
import { buildBaseLayout } from "../../../lib/brandSetup/payload";
import { effectiveFontNames, initialBrandSetupState, type BrandSetupState } from "../../../lib/brandSetup/state";
import type { FontFamilyEntry } from "../../../api";

export function useSampleProps(state: BrandSetupState, libraryFamilies: FontFamilyEntry[]): {
  fontFaces: FontFaceSpec[];
  /** `@font-face` for every face the STEPS preview in the DOM (the phone
   *  registers its own through `fontFaces`). */
  fontFaceCss: string;
  inputProps: ClipProps;
} {
  const fontFaces = useMemo<FontFaceSpec[]>(() => {
    const up = state.fonts.uploaded;
    return [
      ...libraryFamilies.map((f) => ({
        family: f.family,
        weight: f.weight ?? 700,
        src: `/api/fonts/${encodeURIComponent(f.family)}`,
      })),
      ...(up?.dataUrl
        ? [
            { family: up.family, weight: 400, src: up.dataUrl },
            { family: up.family, weight: 700, src: up.dataUrl },
          ]
        : []),
    ];
  }, [libraryFamilies, state.fonts.uploaded]);

  /** @font-face for every face the steps preview in the DOM (the phone
   *  registers its own through `fontFaces`). */
  const fontFaceCss = useMemo(
    () =>
      fontFaces
        .map((f) => `@font-face{font-family:"${f.family}";src:url(${f.src});font-weight:${f.weight};font-display:swap;}`)
        .join("\n"),
    [fontFaces],
  );

  // What sits in the logo's spot until the operator gives us one: the "YOUR
  // LOGO" placeholder, NEVER the Quotologies logo (spec round-2 §2.3 — this is
  // a stranger's brand). The SVG is there from the first frame; the canvas PNG
  // replaces it once drawn (one draw per session, memoised in the module —
  // a later mount starts straight on the PNG).
  const [placeholder, setPlaceholder] = useState(() => cachedLogoPlaceholder() ?? logoPlaceholderSvgDataUrl());
  useEffect(() => {
    let live = true;
    void drawLogoPlaceholder().then((url) => {
      if (live) setPlaceholder(url);
    });
    return () => {
      live = false;
    };
  }, []);

  // Deliberately NOT keyed on designPatch: the phone replays the patch ON TOP
  // of this baseline, so a dependency would rebuild the baseline under the
  // patch on every step (the trap Onboard.tsx documents).
  const { colors, fonts, language, trackingPx, logoDataUrl } = state;
  const inputProps = useMemo<ClipProps>(() => {
    // The LANGUAGE is part of the baseline: it decides the caption shape (the
    // founding two-line punch for RTL, equal lines for everything else).
    const base = buildBaseLayout({ ...initialBrandSetupState(), language, trackingPx });
    const layout: RenderLayout = {
      caption: base.caption as RenderLayout["caption"],
      logo: base.logo!,
      ...(base.speakerCard ? { speakerCard: base.speakerCard } : {}),
    };
    const names = effectiveFontNames({ ...initialBrandSetupState(), language, fonts });
    const props = buildSampleInputProps({
      direction: language.direction,
      glyphRatio: language.glyphRatio,
      captionColor: colors.ink,
      captionAccent: colors.accent,
      speakerNameColor: colors.accent,
      fonts: {
        caption: names.body,
        // Same rule as `buildFontRolesPayload`: punch is the big bottom line
        // in the RTL layout, and only the digit face in the Latin one.
        punch: language.direction === "rtl" ? names.display : names.body,
        speaker: names.body,
        speakerName: names.display,
      },
      fontFaces,
      logoSrc: logoDataUrl || placeholder,
    });
    return { ...props, layout };
  }, [colors, fonts, language, trackingPx, logoDataUrl, placeholder, fontFaces]);

  return { fontFaces, fontFaceCss, inputProps };
}
